// Cloud Terminal Service - Frontend session management with IndexedDB persistence

import { wsClient } from './websocket-client.js';

export type CloudTerminalType = 'claude' | 'shell';
export type CloudTerminalSessionStatus = 'creating' | 'running' | 'closed' | 'error';

export interface ModelEntry {
  id: string;
  provider: string;
  providerDisplayName: string;
  modelDisplayName: string;
}

export interface CloudTerminalModelConfig {
  provider: string;
  model: string;
  displayName: string;
}

export interface CloudTerminalSessionInfo {
  id: string;
  type: CloudTerminalType;
  status: CloudTerminalSessionStatus;
  model?: CloudTerminalModelConfig;
  createdAt: string;
  title?: string;
  cwd?: string;
  agentId?: number;
}

// IndexedDB persistence

const DB_NAME = 'compass-cloud-terminal';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbPut(session: CloudTerminalSessionInfo): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(session);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function dbDelete(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function dbGetAll(): Promise<CloudTerminalSessionInfo[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => { db.close(); resolve(req.result as CloudTerminalSessionInfo[]); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

async function dbClear(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

// Service

class CloudTerminalService {
  private _lastUsedModel: CloudTerminalModelConfig | null = null;
  private _modelsCache: Promise<ModelEntry[]> | null = null;

  async createSession(
    type: CloudTerminalType,
    model?: CloudTerminalModelConfig,
    cwd?: string,
    agentId?: number,
  ): Promise<string> {
    const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    if (model) {
      this._lastUsedModel = model;
      try {
        localStorage.setItem('cloud-terminal:last-model', JSON.stringify(model));
      } catch { /* ignore */ }
    }

    wsClient.send({
      type: 'cloud-terminal:create',
      payload: { type, model, cwd, agentId, requestId },
      timestamp: new Date().toISOString(),
    });

    return requestId;
  }

  async saveSession(session: CloudTerminalSessionInfo): Promise<void> {
    await dbPut(session);
  }

  async getSession(id: string): Promise<CloudTerminalSessionInfo | undefined> {
    const all = await dbGetAll();
    return all.find(s => s.id === id);
  }

  async getAllSessions(): Promise<CloudTerminalSessionInfo[]> {
    return dbGetAll();
  }

  async removeSession(id: string): Promise<void> {
    await dbDelete(id);
  }

  async updateSessionStatus(id: string, status: CloudTerminalSessionStatus): Promise<void> {
    const session = await this.getSession(id);
    if (session) {
      session.status = status;
      await dbPut(session);
    }
  }

  async clearAllSessions(): Promise<void> {
    await dbClear();
  }

  closeSession(sessionId: string): void {
    wsClient.send({
      type: 'cloud-terminal:close',
      payload: { sessionId },
      timestamp: new Date().toISOString(),
    });
  }

  sendInput(sessionId: string, data: string): void {
    wsClient.send({
      type: 'cloud-terminal:input',
      payload: { sessionId, data },
      timestamp: new Date().toISOString(),
    });
  }

  sendResize(sessionId: string, cols: number, rows: number): void {
    wsClient.send({
      type: 'cloud-terminal:resize',
      payload: { sessionId, cols, rows },
      timestamp: new Date().toISOString(),
    });
  }

  requestBuffer(sessionId: string): void {
    wsClient.send({
      type: 'cloud-terminal:buffer-request',
      payload: { sessionId },
      timestamp: new Date().toISOString(),
    });
  }

  fetchAvailableModels(): Promise<ModelEntry[]> {
    if (!this._modelsCache) {
      const promise = fetch('/api/cloud-terminal/models')
        .then(async r => {
          if (!r.ok) throw new Error(`Failed to load models: ${r.status}`);
          const data = await r.json() as { models: ModelEntry[] };
          return data.models;
        });
      promise.catch(() => {
        if (this._modelsCache === promise) this._modelsCache = null;
      });
      this._modelsCache = promise;
    }
    return this._modelsCache;
  }

  getLastUsedModel(): CloudTerminalModelConfig | null {
    if (this._lastUsedModel) return this._lastUsedModel;
    try {
      const raw = localStorage.getItem('cloud-terminal:last-model');
      if (raw) {
        this._lastUsedModel = JSON.parse(raw) as CloudTerminalModelConfig;
        return this._lastUsedModel;
      }
    } catch { /* ignore */ }
    return null;
  }
}

export const cloudTerminalService = new CloudTerminalService();
